export interface ProjectTemplate {
  id: string
  userId: string
  
  // Template Info
  name: string
  description: string
  category: 'wedding' | 'portrait' | 'commercial' | 'event' | 'product' | 'real-estate' | 'custom'
  subcategory?: string // e.g., 'engagement', 'corporate-headshots', etc.
  
  // Template Settings
  estimatedDuration: number // in days
  recommendedTeamSize: number
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced'
  
  // Project Structure
  phases: ProjectPhase[]
  tasks: ProjectTemplateTask[]
  deliverables: ProjectDeliverable[]
  
  // Pricing & Business
  suggestedPricing?: {
    min: number
    max: number
    currency: string
  }
  
  // Meta Information
  tags: string[]
  isPublic: boolean // Can other users see/use this template
  isDefault: boolean // System default template
  usageCount: number // How many times it's been used
  rating?: number // Average user rating
  
  // Timestamps
  createdAt: string
  updatedAt: string
}

export interface ProjectPhase {
  id: string
  name: string
  description: string
  order: number
  estimatedDays: number
  color: string
  
  // Phase requirements
  prerequisites?: string[] // Phase IDs that must be completed first
  deliverables: string[] // Deliverable IDs that belong to this phase
  tasks: string[] // Task IDs that belong to this phase
}

export interface ProjectTemplateTask {
  id: string
  title: string
  description: string
  category: 'pre-production' | 'production' | 'post-production' | 'delivery' | 'admin'
  
  // Scheduling
  estimatedHours: number
  priority: 'low' | 'medium' | 'high' | 'critical'
  phaseId: string
  order: number
  
  // Dependencies
  dependsOn?: string[] // Task IDs that must be completed first
  
  // Assignment
  assignedTo?: 'photographer' | 'editor' | 'assistant' | 'client' | 'vendor'
  skillsRequired?: string[]
  
  // Timing relative to shoot date
  daysBeforeShoot?: number // Negative for before, positive for after
  daysAfterPrevious?: number // Days after previous task completion
  
  // Automation
  isAutomated?: boolean
  automationTrigger?: 'date' | 'task-completion' | 'client-action'
  
  // Resources
  resources?: TaskResource[]
  notes?: string
}

export interface TaskResource {
  id: string
  name: string
  type: 'equipment' | 'software' | 'document' | 'contact' | 'location'
  description?: string
  url?: string
  isRequired: boolean
}

export interface ProjectDeliverable {
  id: string
  name: string
  description: string
  type: 'photos' | 'videos' | 'document' | 'album' | 'digital-gallery' | 'prints'
  
  // Delivery Details
  format?: string // e.g., 'RAW', 'JPEG', 'PDF', 'MP4'
  quality?: string // e.g., 'high-res', 'web-res', '4K'
  quantity?: number
  
  // Timeline
  estimatedDeliveryDays: number
  phaseId: string
  
  // Client Interaction
  requiresClientApproval: boolean
  allowsClientRevisions: boolean
  maxRevisions?: number
  
  // Pricing
  isIncludedInBase: boolean
  additionalCost?: number
}

export interface CreateProjectTemplateData {
  name: string
  description: string
  category: ProjectTemplate['category']
  subcategory?: string
  estimatedDuration: number
  recommendedTeamSize: number
  difficultyLevel: ProjectTemplate['difficultyLevel']
  phases: Omit<ProjectPhase, 'id'>[]
  tasks: Omit<ProjectTemplateTask, 'id'>[]
  deliverables: Omit<ProjectDeliverable, 'id'>[]
  suggestedPricing?: ProjectTemplate['suggestedPricing']
  tags: string[]
  isPublic: boolean
}

export interface ProjectInstance {
  id: string
  userId: string
  templateId: string
  clientId?: string
  shootId?: string
  
  // Project Info
  name: string
  description?: string
  status: 'planning' | 'in-progress' | 'review' | 'completed' | 'cancelled'
  
  // Timeline
  startDate: string
  expectedEndDate: string
  actualEndDate?: string
  shootDate?: string
  
  // Progress
  phases: ProjectInstancePhase[]
  tasks: ProjectInstanceTask[]
  deliverables: ProjectInstanceDeliverable[]
  
  // Financial
  agreedPrice?: number
  paidAmount?: number
  
  // Related Data
  client?: {
    id: string
    name: string
    email: string
    company?: string
  }
  template?: ProjectTemplate
  
  // Timestamps
  createdAt: string
  updatedAt: string
}

export interface ProjectInstancePhase {
  id: string
  templatePhaseId: string
  name: string
  status: 'pending' | 'in-progress' | 'completed' | 'skipped'
  startDate?: string
  endDate?: string
  progress: number // 0-100
}

export interface ProjectInstanceTask {
  id: string
  templateTaskId: string
  title: string
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled'
  assignedUserId?: string
  startDate?: string
  dueDate?: string
  completedDate?: string
  actualHours?: number
  notes?: string
}

export interface ProjectInstanceDeliverable {
  id: string
  templateDeliverableId: string
  name: string
  status: 'pending' | 'in-progress' | 'delivered' | 'approved' | 'revision-required'
  deliveredDate?: string
  approvedDate?: string
  revisionCount: number
  files?: DeliverableFile[]
}

export interface DeliverableFile {
  id: string
  name: string
  url: string
  type: string
  size: number
  uploadedAt: string
}

// Predefined project template categories with examples
export const projectTemplateCategories = {
  wedding: {
    name: 'Wedding Photography',
    icon: '💒',
    subcategories: ['engagement', 'ceremony', 'reception', 'bridal', 'full-wedding'],
    estimatedDuration: 30,
    complexity: 'advanced'
  },
  portrait: {
    name: 'Portrait Photography',
    icon: '📸',
    subcategories: ['individual', 'family', 'corporate-headshots', 'maternity', 'newborn'],
    estimatedDuration: 7,
    complexity: 'intermediate'
  },
  commercial: {
    name: 'Commercial Photography',
    icon: '🏢',
    subcategories: ['product', 'architectural', 'food', 'fashion', 'advertising'],
    estimatedDuration: 14,
    complexity: 'advanced'
  },
  event: {
    name: 'Event Photography',
    icon: '🎉',
    subcategories: ['corporate-event', 'party', 'conference', 'concert', 'sports'],
    estimatedDuration: 5,
    complexity: 'intermediate'
  },
  product: {
    name: 'Product Photography',
    icon: '📦',
    subcategories: ['e-commerce', 'catalog', 'lifestyle', 'jewelry', 'technology'],
    estimatedDuration: 3,
    complexity: 'beginner'
  },
  'real-estate': {
    name: 'Real Estate Photography',
    icon: '🏠',
    subcategories: ['residential', 'commercial', 'aerial', 'virtual-tour'],
    estimatedDuration: 2,
    complexity: 'beginner'
  },
  custom: {
    name: 'Custom Project',
    icon: '⚙️',
    subcategories: [],
    estimatedDuration: 7,
    complexity: 'intermediate'
  }
}

// Common task categories
export const taskCategories = {
  'pre-production': {
    name: 'Pre-Production',
    color: '#3b82f6',
    description: 'Planning and preparation tasks'
  },
  'production': {
    name: 'Production',
    color: '#10b981',
    description: 'Shooting and content creation'
  },
  'post-production': {
    name: 'Post-Production',
    color: '#f59e0b',
    description: 'Editing and processing'
  },
  'delivery': {
    name: 'Delivery',
    color: '#8b5cf6',
    description: 'Final delivery and client handoff'
  },
  'admin': {
    name: 'Administrative',
    color: '#64748b',
    description: 'Business and administrative tasks'
  }
} 